/**
 * The local login (piece 14; decisions 0046, 0194): the break-glass
 * administrator's password and time-based one-time code, entered together on
 * one dedicated page, issuing the build's first real session. The rules here
 * are pure, so the API and the Login Screen read one wording each.
 */
export const LOGIN_ROUTE = '/login';

/** 🔨 The session length is two Settings rows under Settings › Security, never code constants (piece 14). */
export const SESSION_LIFETIME_KEY = 'auth.sessionLifetimeMinutes';
export const SESSION_IDLE_KEY = 'auth.sessionIdleMinutes';
export const DEFAULT_SESSION_LIFETIME_MINUTES = 720;
export const DEFAULT_SESSION_IDLE_MINUTES = 60;
/**
 * 🔒 Decision 4400: a Device-kind session, the one a phone build that holds the lock asks for, lives this many days and
 * slides by the same span on each `auth/session` read; neither key above refuses it. Seeded 90 with piece 21's rows.
 */
export const DEVICE_SESSION_DAYS_KEY = 'mobile.deviceSessionDays';
export const DEFAULT_DEVICE_SESSION_DAYS = 90;
/** The two kinds a session is issued as; the body field a sign-in names its kind in (decision 4400). */
export const SESSION_KINDS = ['Browser', 'Device'] as const;
export type SessionKindName = (typeof SESSION_KINDS)[number];

/** The expiry a Device session is issued or slid to: now plus the days the key names, at least one. */
export function deviceSessionExpiry(now: Date, days: number): Date {
  return new Date(now.getTime() + Math.max(1, days) * 86_400_000);
}

/** The break-glass alert address; unset, the message goes to every login holding the seeded Admin role. */
export const ALERT_EMAIL_KEY = 'security.alertEmail';

/** The two rows the install seeds for this piece, at the defaults the piece names. */
export const PIECE_14_SETTINGS: readonly { key: string; value: number }[] = [
  { key: SESSION_LIFETIME_KEY, value: DEFAULT_SESSION_LIFETIME_MINUTES },
  { key: SESSION_IDLE_KEY, value: DEFAULT_SESSION_IDLE_MINUTES },
];

/** 🔨 The documented default of decision 0046; it can never be set back, checked against the constant itself. */
export const DEFAULT_BREAK_GLASS_PASSWORD = 'ChangeMe!';
export const MIN_PASSWORD_LENGTH = 12;
export const TOTP_DIGITS = 6;

export interface LoginDraft {
  email: string;
  password: string;
  /** The six-digit code; empty until the person is enrolled, which the screen says beside the field. */
  code: string;
  /** Trust this device: the code just verified stands for the second factor on it for the set number of days (decision 0324). */
  trustDevice: boolean;
}

export function emptyLogin(): LoginDraft {
  return { email: '', password: '', code: '', trustDevice: false };
}

/** What stops the form submitting; the server's own refusal is a message, not a problem here. */
export function loginProblems(draft: LoginDraft): string[] {
  const problems: string[] = [];
  if (!draft.email.trim()) problems.push('email');
  if (!draft.password) problems.push('password');
  if (draft.code && !/^\d{6}$/.test(draft.code.trim())) problems.push('code');
  return problems;
}

/** 🔨 Setting the password back to the documented default is refused; a short one is refused with it. */
export function passwordProblems(next: string): string[] {
  const problems: string[] = [];
  if (next === DEFAULT_BREAK_GLASS_PASSWORD) problems.push('default');
  if (next.length < MIN_PASSWORD_LENGTH) problems.push('length');
  return problems;
}

export function isDefaultPassword(password: string): boolean {
  return password === DEFAULT_BREAK_GLASS_PASSWORD;
}

/**
 * 🔨 The default password is never accepted from a non-loopback address until
 * it is changed, which keeps a fresh install off the internet during the
 * window a shipped constant creates (piece 14).
 */
export function isLoopbackAddress(address: string | null | undefined): boolean {
  if (!address) return false;
  const bare = address.trim().replace(/^\[|\]$/g, '').toLowerCase();
  if (bare === 'localhost' || bare === '::1') return true;
  const v4 = bare.startsWith('::ffff:') ? bare.slice(7) : bare;
  return /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(v4);
}

/** The expiry a session is issued with, computed at login from the lifetime key. */
export function sessionExpiry(issuedAt: Date, lifetimeMinutes: number): Date {
  return new Date(issuedAt.getTime() + Math.max(1, lifetimeMinutes) * 60_000);
}

export interface SessionFacts {
  expiresAt: Date;
  lastActivityAt: Date;
  revokedAt?: Date | null;
  /** Browser where absent; a Device session is never idle (decision 4400; piece 21). */
  kind?: SessionKindName;
}

export type SessionState = 'live' | 'revoked' | 'expired' | 'idle';

/**
 * 🔨 `expiresAt` is enforced on every request, and a Browser session idle longer than the idle key is refused as well.
 * A Device session answers to its own `expiresAt` alone, which its reads slide (piece 21; decision 4400).
 */
export function sessionState(session: SessionFacts, now: Date, idleMinutes: number): SessionState {
  if (session.revokedAt) return 'revoked';
  if (session.expiresAt.getTime() <= now.getTime()) return 'expired';
  if (session.kind === 'Device') return 'live';
  if (now.getTime() - session.lastActivityAt.getTime() > Math.max(1, idleMinutes) * 60_000) return 'idle';
  return 'live';
}

/** 🔨 First login on the default credential can do exactly one thing, set a new password; every other route is refused. */
export const ROUTES_OPEN_BEFORE_PASSWORD_CHANGE = ['/auth/set-password', '/auth/sign-out', '/auth/session'] as const;

/**
 * 🔒 Decision 1505: the path a route is declared at. The query and a trailing slash go, and so does the single `/api`
 * segment a proxy in front of the API may leave on. Every allowlist below is matched against this and never by suffix,
 * because a suffix lets any path that merely ends in an allowed route, `/pages/tags/auth/session`, pass for it.
 */
export function routedPath(path: string): string {
  const bare = (path.split('?')[0] ?? path).replace(/\/+$/, '');
  return bare.replace(/^\/api(?=\/|$)/, '');
}

export function routeOpenBeforePasswordChange(path: string): boolean {
  const routed = routedPath(path);
  return (ROUTES_OPEN_BEFORE_PASSWORD_CHANGE as readonly string[]).includes(routed);
}

/** The answers the sign-in route gives; the screen branches on `kind` and nothing else. */
export type SignInOutcome =
  | { kind: 'session'; token: string; expiresAt: string; mustChangePassword: boolean; /** Issued once, at enrolment, and shown once (piece 21). */ recoveryCodes?: readonly string[]; /** The device's proof, issued when the person asked to trust it (decision 0324). */ deviceToken?: string; deviceExpiresAt?: string }
  /** The password held and no factor is enrolled yet: the screen shows the secret and asks for the first code. */
  | { kind: 'enrol'; enrolmentToken: string; secret: string; otpauthUrl: string }
  | { kind: 'refused'; reason: string }
  /** The password held and a factor is enrolled: the second step asks for the code against this short-lived token (decision 0325), or a passkey where the login holds one (decision 0351). */
  | { kind: 'challenge'; challengeToken: string; trustedDeviceDays: number; passkey?: boolean }
  /** 🔨 Lockout names the seconds left, so the screen counts down from the real window and disables Sign In until it clears (piece 21). */
  | { kind: 'locked'; secondsLeft: number; reason: string };

/** What redeeming an invitation link or an activation code answers: the pre-authentication token the set-password screen carries (piece 21). */
export type RedeemOutcome =
  | { kind: 'activate'; activationToken: string; email: string }
  | { kind: 'refused'; reason: string };

/** The Authorization header the browser sends; the API reads the bearer token and nothing else from it. */
export function bearerFrom(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const match = /^Bearer\s+(\S+)$/i.exec(header.trim());
  return match ? match[1]! : null;
}

/** The HttpOnly cookie the browser's session rides in (decision 0310); a script never reads it, and an API client sends the bearer instead. */
export const SESSION_COOKIE_NAME = 'bms2_session';
/** The trusted device's cookie, HttpOnly beside the session's and outliving it (decision 0324). */
export const DEVICE_COOKIE_NAME = 'bms2_device';
/** The single sign-on flow's cookie (decision 1254): the hash of the state the start issued, so a callback is honoured only in the browser that began it. */
export const SSO_FLOW_COOKIE_NAME = 'bms2_sso_flow';

/** One named cookie's value from a Cookie header, or null where it is absent or empty. */
export function cookieValueFrom(header: unknown, cookieName: string): string | null {
  if (typeof header !== 'string') return null;
  for (const part of header.split(';')) {
    const [name, ...rest] = part.split('=');
    if (name?.trim() !== cookieName) continue;
    const value = decodeURIComponent(rest.join('=').trim());
    return value === '' ? null : value;
  }
  return null;
}

/** The session token from a Cookie header, or null where the cookie is absent or empty. */
export function cookieTokenFrom(header: unknown): string | null {
  return cookieValueFrom(header, SESSION_COOKIE_NAME);
}

/**
 * 🔨 With enforcement on, a request carrying no session reaches these routes
 * and nothing else (piece 21; decision 0304): the health check and the
 * readiness answer the container's own check and the proxy read, which carry
 * no login (decision 2570; the first swap deploy of 2026-10-08 waited fifteen
 * minutes on a 401), the sign-in
 * and its follow-ups, the passkey's second step beside the code's (decision
 * 0351), an invitation's redemption, a reset request, the website's lead
 * intake, and a share link's stream, which carries its own token. Everything
 * else answers 401 before any gate runs. The e-signature vendor's completion
 * callback is open too, and its digest over the raw body is its whole gate
 * (decision 1505), and so is the payment processor's webhook (decision 1649).
 * The text carrier's inbound reply and delivery receipt are open the same way,
 * and the carrier's signature over the URL and the posted fields is their whole
 * gate, a 403 in the controller (decision 5120). The three single sign-on routes are
 * the sign-in's own (decision 0352): the status the Login Screen draws the
 * button from, the start and the callback.
 */
export const ROUTES_OPEN_WITHOUT_SESSION = ['/health', '/health/ready', '/auth/status', '/auth/sign-in', '/auth/verify-code', '/auth/verify-passkey/options', '/auth/verify-passkey', '/auth/enroll', '/auth/redeem', '/auth/activate', '/auth/reset-request', '/auth/session', '/public/leads', '/auth/sso/status', '/auth/sso/start', '/auth/sso/callback', '/e-signature/webhook', '/payments/stripe/webhook', '/texts/inbound', '/texts/status'] as const;

export function routeOpenWithoutSession(path: string): boolean {
  const routed = routedPath(path);
  if (/^\/files\/share\/[^/]+\/stream$/.test(routed)) return true;
  // 🔨 Decision 0679: the certificate request link's page facts and its one upload, guarded by the token alone.
  if (/^\/public\/certificate-requests\/[^/]+(\/upload)?$/.test(routed)) return true;
  // 🔨 Piece 32 (decision 0162): the open-tracking link, guarded by its opaque token and the per-address limit.
  if (/^\/public\/mail\/open\/[^/]+$/.test(routed)) return true;
  // 🔨 Decision 2650: the Quote link's page, its PDF and its two answers, guarded by the token, its revision, its expiry and the per-address limit.
  if (/^\/public\/quote-links\/[^/]+(\/(pdf|accept|decline))?$/.test(routed)) return true;
  // 🔨 Decision 5130: the carrier's four call callbacks, guarded by the provider's own signature over the URL and the posted fields, never a session.
  if (/^\/calls\/(answer|status|recording|transcription)$/.test(routed)) return true;
  // 🔨 Decision 11780: the inbound call's five, every one under calls/inbound, so no session read of the same name is opened with them.
  if (/^\/calls\/inbound(\/(ring-status|menu|voicemail|voicemail\/transcription))?$/.test(routed)) return true;
  // 🔨 Decision 12001: the TwiML App's post for a call placed in the phone app, verified by the provider's signature.
  if (/^\/calls\/outbound$/.test(routed)) return true;
  // 🔨 Decision 12502: the plant feed's HTTP push, guarded by the site's key alone, checked as the feed role before the body is read.
  if (/^\/feed\/[^/]+\/(readings|faults)$/.test(routed)) return true;
  return (ROUTES_OPEN_WITHOUT_SESSION as readonly string[]).includes(routed);
}

/** 🔒 Decision 1505: the sentence a write from another site is refused with. */
export const CROSS_ORIGIN_COPY = 'This request came from another site, so it was refused; use the application at its own address.';

/**
 * 🔒 Decision 1505: a write from a browser page on another origin. A session cookie is SameSite=Lax, which holds a page
 * on another site and not a page on another host of the same registrable domain, so the request's own `Origin` is
 * asked too. A request carrying none is not a browser's cross-site request (a script, a vendor's callback, a suite)
 * and is let through, and so is every route open without a session, which the website's lead form posts to from its
 * own origin.
 */
export function crossOriginWriteRefused(request: { method?: string; origin?: unknown; path: string }, appBaseUrl: string): boolean {
  const method = (request.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return false;
  if (typeof request.origin !== 'string' || request.origin === '') return false;
  if (routeOpenWithoutSession(request.path)) return false;
  try {
    return request.origin !== new URL(appBaseUrl).origin;
  } catch {
    return false;
  }
}

/**
 * 🔨 In sandbox mode a request naming nobody is promoted to the seeded
 * developer on the ordinary routes alone (piece 11; decision 0349). These
 * take a session or the developer's explicit person header, and a request
 * carrying neither answers 401 before any gate runs: Reset to fixtures, the
 * outbox, Server info's timings, log and server reads (decision 1529), the
 * audit search, the settings values, every screening route and a file's own
 * stream. The share link's stream stays open on its own token, and
 * `/health` stays open with its install block withheld (piece 21).
 */
export const ROUTES_CONFINED_IN_SANDBOX = ['/dev/reset', '/dev/outbox', '/system-timings', '/system-log', '/system-log/export', '/system-scanner-log', '/system-scanner-log/export', '/system-server', '/audit/search', '/settings/values'] as const;

export function routeConfinedInSandbox(path: string): boolean {
  const bare = (path.split('?')[0] ?? path).replace(/\/+$/, '');
  if (/\/files\/[^/]+\/stream$/.test(bare) && !/\/files\/share\//.test(bare)) return true;
  if (/\/files\/[^/]+\/versions\/[^/]+\/stream$/.test(bare)) return true;
  if (bare.endsWith('/screening') || /\/screening\//.test(bare)) return true;
  return ROUTES_CONFINED_IN_SANDBOX.some((route) => bare === route || bare.endsWith(route));
}

/** The event the frame raises when a request carrying a session is refused for want of one, so the banner re-authenticates in place (piece 21). */
export const SESSION_EXPIRED_EVENT = 'bms2:session-expired';
export const SESSION_RENEWED_EVENT = 'bms2:session-renewed';
export const SESSION_EXPIRED_COPY = 'Your session has expired. Your unsaved changes are kept — sign in to continue.';
